import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { SectionHeading } from "@/components/ui-ext/SectionHeading";
import { CASE_STUDIES } from "@/content/caseStudies";
import { Picture } from "@/components/media/Picture";

const CASE_STUDY_FAQS = [
  {
    q: "What's the difference between a portfolio entry and a full case study?",
    a: "A portfolio entry documents an outcome — the metrics, the timeframe, the sourced screenshots. A case study documents the reasoning: the state of the site before we started, the hypothesis we formed, the specific changes we made in what order, and how the results tracked (or didn't) against what we predicted. It's the difference between a results slide and the working notes behind it.",
  },
  {
    q: "How do you set a baseline before claiming a result?",
    a: "We pull a Search Console and Semrush snapshot in the first week of an engagement, before any technical or content changes ship, and we log the site's existing structure, indexation status, and any obvious technical debt. That snapshot becomes the fixed reference point for every comparison in the case study. If a client approaches us after already making changes on their own, we say so and adjust which portion of the timeline we're willing to attribute to our work.",
  },
  {
    q: "How do you handle attribution when multiple things changed at once?",
    a: "Honestly, and imperfectly. Most real engagements involve technical fixes, content additions, and link building happening in overlapping windows, plus external factors like algorithm updates or a client's own paid campaigns. We describe the sequence of changes and flag which results plausibly map to which change, but we don't claim false precision — if we can't isolate a single variable, we say the result reflects the combined programme rather than inventing a controlled experiment that didn't happen.",
  },
  {
    q: "Do your case studies mention things that didn't work?",
    a: "Yes, when they're material to the story. If a content push under-performed relative to a technical fix, or a page type never earned the rankings we expected, that's part of the methodology write-up. Omitting the misses would make the case studies read like marketing copy rather than a genuine account of the engagement, and it would deprive readers of the more useful lesson — knowing what didn't work is often more transferable than knowing what did.",
  },
  {
    q: "How long after an engagement do you publish a case study?",
    a: "Typically after we have at least two to three months of stable post-change data, so we're not reporting on a temporary spike from a single algorithm update or seasonal event. For engagements with strong compounding effects, such as authority-building programmes, we sometimes wait longer and publish an update later rather than freezing the story at an artificially early high point.",
  },
  {
    q: "What should I be sceptical of when I read any agency's case study?",
    a: "A case study with no stated starting point, a single screenshot with no visible date, or language like \"we generated millions in revenue\" with no attribution methodology explained. Also be wary of case studies that read as pure narrative with numbers dropped in decoratively — a credible one should let you reconstruct roughly what happened and when, not just what the agency wants you to conclude.",
  },
  {
    q: "Can I request access to the underlying data for a case study before we work together?",
    a: "For engagements where the client has agreed to it, yes — we'll share a redacted or read-only view of the relevant Search Console property on a call. It's a reasonable ask of any agency, and one that separates agencies willing to stand behind their published numbers from those that aren't.",
  },
];


export const Route = createFileRoute("/case-studies/")({
  head: () => ({ ...pageMeta({
    path: "/case-studies/",
    title: "SEO Case Studies — Long-Form Teardowns with Real Data | Optimize Plus",
    description:
      "Long-form SEO case studies from Optimize Plus. Every programme, target, tactic, and outcome — sourced from Semrush and Google Search Console with dated screenshots and named clients.",
  }), scripts: ldScripts(
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Case Studies", path: "/case-studies/" }]),
    faqLd(CASE_STUDY_FAQS),
  ) }),
  component: CaseStudiesIndex,
});

function CaseStudiesIndex() {
  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1} className="container-page py-20 md:py-28">
        <SectionHeading
          as="h1"
          eyebrow="Case Studies"
          title="Investigative reports from the field."
          intro="Long-form breakdowns of how we build growth engines. Every claim is sourced. Every result is dated. Every screenshot is real."
        />
        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {CASE_STUDIES.map((c) => (
            <Link
              key={c.slug}
              to="/case-studies/$slug/"
              params={{ slug: c.slug }}
              className="group surface-card overflow-hidden transition-transform hover:-translate-y-1"
            >
              <div className="aspect-[16/9] overflow-hidden bg-surface-2">
                <Picture
                  variantKey={c.project.coverVariant}
                  alt=""
                  sizes="(min-width: 768px) 45vw, 100vw"
                  className="block h-full w-full"
                  imgClassName="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="p-6">
                <div className="text-[0.7rem] uppercase tracking-widest text-primary">
                  {c.hero.eyebrow} · {c.readingTime}
                </div>
                <h2 className="mt-3 font-display text-2xl leading-tight">
                  {c.hero.headline}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">{c.hero.deck}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {c.outcome.slice(0, 3).map((o) => (
                    <span
                      key={o}
                      className="text-[0.7rem] rounded-full border border-border px-2.5 py-0.5 text-muted-foreground"
                    >
                      {o}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <section className="mt-24 max-w-3xl border-t border-border pt-16">
          <div className="text-[0.65rem] uppercase tracking-widest text-primary">Methodology</div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            How we build a case study
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              Every case study on this site follows the same skeleton: the client's situation before we
              arrived, the specific hypothesis behind our first ninety days of work, the sequence of
              changes we made, and an honest read of the results against that hypothesis. We write the
              baseline section first and lock it before any results are known, so the "before" picture
              isn't reverse-engineered to flatter the "after" one.
            </p>
            <p>
              We source every figure the same way we do on portfolio entries — Search Console exports,
              Semrush snapshots, Google Business Profile insights, or client-provided revenue and lead
              data where the client has agreed to share it. Where a number can't be sourced, it doesn't
              appear, even if it would make the story read better.
            </p>
            <p>
              A finished case study goes back to the client for a factual check before publication. This
              isn't about softening the language — it's about catching errors, confirming we haven't
              disclosed anything commercially sensitive, and making sure the timeline we describe matches
              what actually happened on their end, including any changes the client's own team made that
              we should credit rather than absorb into our own numbers.
            </p>
          </div>
        </section>

        <section className="mt-16 max-w-3xl border-t border-border pt-16">
          <div className="text-[0.65rem] uppercase tracking-widest text-primary">Baselines</div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Setting a baseline that survives scrutiny
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              A baseline is only useful if it's captured before intervention and dated precisely. We
              take our starting snapshot in the first week of an engagement — organic keyword count,
              Search Console impressions and clicks, indexation status in Google Search Console, and a
              technical audit of crawl errors, page speed, and schema coverage. This gets logged
              internally even for engagements that never become a public case study, because it protects
              both the client and us if questions come up later about what changed and when.
            </p>
            <p>
              For clients who come to us mid-campaign — say, after a previous agency's work, or after
              they've already made changes themselves — we're explicit in the case study about which
              portion of the history is theirs and which is ours. Claiming credit for gains that predate
              our involvement is one of the more common ways agency case studies mislead, even
              unintentionally, and we build the baseline section specifically to avoid it.
            </p>
          </div>
        </section>

        <section className="mt-16 max-w-3xl border-t border-border pt-16">
          <div className="text-[0.65rem] uppercase tracking-widest text-primary">Attribution</div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Attribution honesty: what we will and won't claim
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              SEO rarely happens in a vacuum. A client's paid search spend, seasonal demand, a
              competitor closing, or a core algorithm update can all move organic performance during the
              same window we're doing our own work. Where we're aware of a confounding factor, we name it
              in the case study rather than let a reader assume our tactics alone produced the entire
              swing. This is slower and less flattering to write than a clean before/after narrative, but
              it's the only version that holds up if a prospective client asks pointed follow-up
              questions.
            </p>
            <p>
              When we can isolate a specific change — a technical fix that resolved an indexation
              problem, or a content cluster that captured new query volume — we say so plainly and point
              to the corresponding Search Console query data. When we can't isolate it, we describe the
              result as attributable to the combined programme, which is the accurate description even
              though it's a less punchy sentence.
            </p>
          </div>
        </section>

        <section className="mt-16 max-w-3xl border-t border-border pt-16">
          <div className="text-[0.65rem] uppercase tracking-widest text-primary">For readers</div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            What to look for as a reader
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              Read for structure before you read for numbers. A trustworthy case study should let you
              answer: what was the site's condition before the engagement, what specifically changed,
              over what timeframe, and what happened afterwards that can plausibly be tied to those
              changes. If any of those four pieces is missing — especially the starting condition — treat
              the headline result with proportionate scepticism regardless of which agency published it.
            </p>
            <p>
              Watch for language that implies certainty an outside observer can't verify, like precise
              attributed revenue figures with no methodology explained, or claims that a single blog post
              "generated" a specific dollar amount months later. And notice whether the write-up
              acknowledges anything that didn't go as planned; a case study with zero friction anywhere in
              the story is more likely edited for effect than a faithful account.
            </p>
          </div>
        </section>

        <section className="mt-16 max-w-3xl border-t border-border pt-16">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Frequently asked questions
          </h2>
          <dl className="mt-8 space-y-6">
            {CASE_STUDY_FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
